import { useState, useMemo, useEffect } from 'react';
import { F, mr } from '../styles/theme';
import { useT } from '../i18n/useT';
import { useTheme } from '../context/ThemeContext';
import { useMarketData } from '../context/MarketDataContext';
import { COIN_MAP, fetchMarketData } from '../utils/coingecko';
import { fmt, fmtC } from '../utils/format';
import { computeOverallConfidence, validateTransactions } from '../utils/importValidation';
import { saveOrUpdateTemplate, completeImportReview } from '../services/documentIngestion';
import { saveCorrections, saveTypeSynonym } from '../services/learningStore';
import { CATEGORY_LABELS, getCategory } from '../utils/transactionTypes';
import { computeAutoFixes, applyAutoFix, isStaleReplaceable, coinsOfTx } from '../utils/reviewUtils';
import { auditImport } from '../utils/reconcile';
import { buildDataCheck } from '../utils/dataCheck';
import { annotateTx, contentHash as fpContentHash } from '../utils/txFingerprint';
import { findMoonPayLedgerDuplicates, findSwapWalletInPairs } from '../utils/moonpayLedgerDedupe';
import { PARSER_VERSION } from '../utils/simpleCsvImporter';
import ExchangeLogo from '../components/ExchangeLogo';
import CoinImage from '../components/CoinImage';
import { registerTokenIcons } from '../utils/tokenIcons';
import { enrichMissingPrices } from '../utils/swapPriceEnricher';
import InfoBanner from '../components/InfoBanner';
import { Icon, Icons } from '../components/red/Icons';
import { Segmented } from '../components/red/Editorial';
import TypeBadge from '../components/red/TypeBadge';
import { TransactionEditScreen } from './TransactionWizard';

const IMPORT_FIAT_SET = new Set(['EUR','USD','GBP','CHF','AUD','CAD','JPY','TRY','BRL']);
function getTxImportCategory(tx) {
  const base = getCategory(tx.typ);
  if (base === 'transfer') {
    const coin = String(tx.coin || tx.sym || '').toUpperCase();
    if (IMPORT_FIAT_SET.has(coin)) return 'fiat';
  }
  return base;
}

// ── CONFIDENCE BADGE ──────────────────────────────────────────────────────────

function ConfidenceBadge({ value }) {
  const { T } = useTheme();
  const color = value >= 0.82 ? T.green : value >= 0.55 ? '#F5A623' : T.red;
  return (
    <span style={{
      fontSize: 9, fontWeight: 800, padding: '2px 5px', borderRadius: mr(16),
      color, background: 'transparent', fontFamily: F.family, letterSpacing: 0.3,
    }}>
      {Math.round(value * 100)}%
    </span>
  );
}

// ── SUMMARY (Marken-Prägedruck) ───────────────────────────────────────────────
// Theme-sicherer Letterpress (identische Licht/Schatten-Werte wie EmbossPct):
// die Zahl ist ins warme Papier (hell) bzw. Anthrazit (dunkel) gepresst.
function embossPress(T) {
  return T.isDark
    ? { color: '#212429', textShadow: '0 1px 0 rgba(255,255,255,0.10), 0 -1.5px 2px rgba(0,0,0,0.75)' }
    : { color: '#D7CFBE', textShadow: '0 1px 0 rgba(255,255,255,0.95), 0 -1.5px 1px rgba(60,48,28,0.30), 0 1px 2px rgba(60,48,28,0.10)' };
}

function ImportSummary({ txs, overallConfidence, deterministic = false }) {
  const { T } = useTheme();
  const { t } = useT();
  // Deterministische On-Chain-Importe haben keine KI-Validierung (_status) → alle gültig.
  const warn   = deterministic ? 0 : txs.filter(t => t._status === 'warning').length;
  const errors = deterministic ? 0 : txs.filter(t => t._status === 'error').length;
  const press  = embossPress(T);
  // Punkt-Signal: grün = bereit/sicher, gold = teils prüfen, rot = Fehler.
  // (Grün/Rot bewusst — rot heißt „Achtung", deshalb NICHT als Default.)
  const dotColor = errors > 0 ? T.red : warn > 0 ? (T.gold || '#F5A623') : (T.gain || T.green);
  // Positiv zuerst: „X sauber erkannt" statt „Bereit". Fehler bleiben die Ausnahme,
  // die kurz Aufmerksamkeit braucht; alltägliche Daten-Lücken zeigt der Datencheck.
  const okCount = Math.max(0, txs.length - warn - errors);
  const subLine = errors > 0
    ? (errors === 1 ? t('imp_needsAttention_one', { n: errors }) : t('imp_needsAttention_other', { n: errors }))
    : warn > 0
      ? t('imp_recognized_some', { ok: okCount, n: warn })
      : t('imp_recognized_clean', { n: txs.length });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 22 }}>
      <span role="img" aria-label={String(txs.length)} style={{
        fontFamily: F.num || F.display, fontWeight: 800, fontSize: 64, lineHeight: 0.82,
        letterSpacing: '-0.04em', fontVariantNumeric: 'tabular-nums',
        color: press.color, textShadow: press.textShadow, userSelect: 'none', flexShrink: 0,
      }}>{txs.length}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontFamily: F.display, fontSize: 18, fontWeight: 700,
          letterSpacing: '-0.02em', color: T.text,
        }}>
          {t('imp_transactions_checked')}
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginTop: 5,
          fontSize: 13, color: T.sub, fontFamily: F.family,
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
          {subLine}
        </div>
      </div>
    </div>
  );
}


// ── MAIN COMPONENT ────────────────────────────────────────────────────────────

export default function ImportReviewScreen({
  result,            // API-Response von analyzeDocument()
  fileName,
  uid,
  existingTxs = [], // bereits in Firestore vorhandene TX (für Re-Import-Schutz)
  onImport,          // (validTxs) => void — schreibt in DB
  onCancel,
}) {
  const { T } = useTheme();
  const { t } = useT();
  const [filter, setFilter]         = useState('all');
  const [category, setCategory]     = useState('all'); // 'all'|'trade'|'transfer'|'fiat'|'income'|'derivatives'|'other'
  const [loading, setLoading]       = useState(false);
  const [visibleCount, setVisible]  = useState(100);
  const [editingTx, setEditingTx]   = useState(null); // TX im Voll-Editor (oder null)
  const [expandedRaw, setExpandedRaw] = useState(new Set());
  const [reviewStatuses, setReviewStatuses] = useState({}); // editId → 'approved'|'skipped'|'force'
  const [txs, setTxs] = useState(() =>
    (result.transactions || []).map((tx, i) => {
      const ann = annotateTx(tx, { source: 'csv' });
      return { ...ann, _editId: i };
    })
  );

  // On-Chain-SPL-Icons (coinIcon* aus dem Solana-Import) in die Symbol→URL-Map
  // eintragen, damit CoinImage sie schon in der Vorschau zeigt (nicht erst nach
  // dem Speichern, wenn useTransactions sie beim Load registriert).
  useEffect(() => { registerTokenIcons(txs); }, [txs]);

  // Preis-Bewertung: bewertbare TX (Swaps, Income, Ein-/Auszahlungen) mit preis=0
  // aus der bekannten Seite × historischem Tagespreis füllen — robuster client-
  // seitiger Fallback, wenn der serverseitige Abruf durchfiel. Vorschau zeigt den
  // Wert; persistierter preis macht Swap-Kostenbasis korrekt (Transfer/gv unberührt).
  // Einmal beim Mount; merged per _editId zurück (parallele Edits nicht überschreiben).
  useEffect(() => {
    let alive = true;
    (async () => {
      const work = txs.map(t => ({ ...t }));
      const n = await enrichMissingPrices(work, { currency: 'eur' });
      if (!alive || n === 0) return;
      const byId = new Map(work.map(t => [t._editId, t]));
      setTxs(prev => prev.map(t => {
        const w = byId.get(t._editId);
        return (w && Number(w.preis) > 0 && !(Number(t.preis) > 0))
          ? { ...t, preis: w.preis, _priceDerived: true }
          : t;
      }));
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hash-Sets bestehender TX — für Re-Import-Konflikt-Erkennung
  const existingContentHashes = useMemo(
    () => new Set(existingTxs.map(t => t.contentHash || fpContentHash(t)).filter(Boolean)),
    [existingTxs]
  );
  const existingChainHashes = useMemo(
    () => new Set(existingTxs.map(t => t.hash).filter(Boolean)),
    [existingTxs]
  );

  // Stale-Parser-Version-Detection: TXs derselben Quelle (= exchange) die mit
  // einer kleineren parserVersion importiert wurden (oder ganz ohne Stempel =
  // pre-versioning, vor 2026-05). Wenn welche gefunden werden, bieten wir dem
  // User einen One-Click-Replace-Flow an statt der contentHash-Dedup-Falle.
  //
  // Beispiel: alter Binance-Parser hat 2 SHIB-Verkäufe vom 2021-10-28 12:59:03
  // zu einem TX kollabiert. Neuer Parser produziert beide TXs einzeln. Ihre
  // contentHashes unterscheiden sich, also greift Dedup nicht — aber die
  // alte falsche TX bleibt im Datensatz. parserVersion-Match löst das.
  const sourceExchange = String(result?.exchange || '').toLowerCase();

  // Coins, die der neue Import tatsächlich liefert. Der Replace-Wipe darf NUR
  // diese Coins anfassen — Börsen-Exports sind keine Obermenge der Historie
  // (Ledger lässt 0-Saldo-Assets weg). Ohne Scope löschte Replace die gematchte
  // Ledger→Bitget-SUI-Auszahlung mit → Phantom-Bestand. Siehe isStaleReplaceable.
  const importCoins = useMemo(() => {
    const s = new Set();
    txs.forEach(t => coinsOfTx(t).forEach(c => s.add(c)));
    return s;
  }, [txs]);

  const staleExistingTxs = useMemo(() => {
    if (!sourceExchange || sourceExchange === 'unknown' || sourceExchange === 'generic') return [];
    return existingTxs.filter(t =>
      isStaleReplaceable(t, { sourceExchange, importCoins, parserVersion: PARSER_VERSION })
    );
  }, [existingTxs, sourceExchange, importCoins]);

  // IDs der staleen TXs — werden beim Replace-Import an handleImportCsv mit
  // wipeIds übergeben, damit App.js sie vor addBatch löscht.
  const staleWipeIds = useMemo(
    () => staleExistingTxs.map(t => t._docId || String(t.id)).filter(Boolean),
    [staleExistingTxs]
  );

  // Pro TX: schon importiert?  + Präzisionsstufe
  const conflictIds = useMemo(() => {
    const s = new Set();
    txs.forEach(t => {
      if (t.hash && existingChainHashes.has(t.hash)) s.add(t._editId);
      else if (t.contentHash && existingContentHashes.has(t.contentHash)) s.add(t._editId);
    });
    return s;
  }, [txs, existingContentHashes, existingChainHashes]);

  // MoonPay-Duplikate: neue Ledger-`einzahlung`, die einen MoonPay-`kauf` doppelt.
  // Sowohl bestehender Bestand als auch parallel importierte MoonPay-Käufe gelten als Referenz.
  const moonpayDuplicates = useMemo(
    () => findMoonPayLedgerDuplicates(txs, existingTxs),
    [txs, existingTxs]
  );
  const moonpayDuplicateIds = useMemo(
    () => new Set(moonpayDuplicates.keys()),
    [moonpayDuplicates]
  );

  // Swap-Settlement-Duplikate: On-Chain-Wallet-Eingang, der den Output eines
  // Swaps doppelt (z.B. SOL→ONDO im Ledger + späterer ETH-Adress-Import).
  const swapDuplicateIds = useMemo(() => {
    const pairs = findSwapWalletInPairs([...existingTxs, ...txs]);
    const candidateSet = new Set(txs);
    const ids = new Set();
    for (const { walletIn } of pairs) {
      if (!candidateSet.has(walletIn)) continue;
      ids.add(walletIn._editId !== undefined ? walletIn._editId : walletIn.id);
    }
    return ids;
  }, [txs, existingTxs]);

  // Zusammengeführt = MoonPay-Lieferung + Swap-Settlement-Duplikate (werden in
  // addBatch in den Kauf/Swap gemergt). Für den Status-Kategorie-Filter.
  const mergedIds = useMemo(
    () => new Set([...moonpayDuplicateIds, ...swapDuplicateIds]),
    [moonpayDuplicateIds, swapDuplicateIds]
  );

  // On-Chain-Wallet-Import (deterministische Daten) → keine KI-Konfidenz-Anzeige.
  const deterministic = !!result?.deterministic;

  // (Entfernt: lowPrecisionIds / dangerousDayIds / collisionGroups — diese
  // technischen Warn-Banner zeigen wir dem Kunden nicht mehr. Die FIFO-Reihenfolge
  // ist deterministisch abgesichert, Duplikate fängt die contentHash-Dedup.)

  // Basis-Marktdaten aus dem App-Context (Bestands-Coins).
  // Coins, die nur im neuen Import vorkommen, fehlen dort — die ziehen wir
  // einmalig nach und mergen lokal.
  const { marketData: contextMarketData } = useMarketData();
  const [extraMarketData, setExtraMarketData] = useState({});
  const marketData = useMemo(
    () => ({ ...contextMarketData, ...extraMarketData }),
    [contextMarketData, extraMarketData]
  );

  useEffect(() => {
    const syms = new Set();
    txs.forEach(t => {
      [t.coin, t.sym, t.coinIn, t.coinOut, t.symIn, t.symOut].forEach(s => {
        if (s) syms.add(String(s).toUpperCase());
      });
    });
    const missingIds = Array.from(syms)
      .map(s => COIN_MAP[s]?.id)
      .filter(id => id && !contextMarketData[id]);
    if (!missingIds.length) return;
    let cancelled = false;
    fetchMarketData(missingIds, 'eur').then(data => {
      if (!cancelled && data && Object.keys(data).length) setExtraMarketData(prev => ({ ...prev, ...data }));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMarketData]);

  const overallConf = computeOverallConfidence(txs);
  // Kombi-Filter: Status (ok/warning/error) + Kategorie (trade/transfer/income/derivatives/other)
  const filtered = useMemo(() => {
    let out = txs;
    if (filter !== 'all') out = out.filter(t => t._status === filter);
    // Status-Kategorien (Import-Status) vor den Typ-Kategorien.
    if (category === 'existing')    out = out.filter(t => conflictIds.has(t._editId));
    else if (category === 'merged') out = out.filter(t => mergedIds.has(t._editId));
    else if (category !== 'all')    out = out.filter(t => getTxImportCategory(t) === category);
    return out;
  }, [txs, filter, category, conflictIds, mergedIds]);
  const validTxs = txs.filter(t => t._status !== 'error');

  // Automatischer Konsistenz-Audit (auditImport) — die gefährlichsten Fehler
  // erkennt die Software an SICH SELBST, ohne dass der User echte Salden kennt:
  // Reihenfolge-Abhängigkeit (Berechnungsfehler), Negativbestand (fehlende
  // Quelle), preislose Trades (Format nicht erkannt). „Laut statt still"
  // (doctrine_release_readiness). Nur warnend — blockt den Import NICHT.
  // Geprüft wird die NETTO-Menge, die wirklich landet (Fehler/Skips/Konflikte
  // raus), damit übersprungene Zeilen keinen Phantom-Negativbestand auslösen.
  const auditableNewTxs = useMemo(() => txs.filter(t => {
    if (t._status === 'error') return false;
    if (reviewStatuses[t._editId] === 'skipped') return false;
    if (conflictIds.has(t._editId) && reviewStatuses[t._editId] !== 'force') return false;
    return true;
  }), [txs, reviewStatuses, conflictIds]);

  // Der Import-Screen bleibt RUHIG. Die alltäglichen Befunde (Negativbestand aus
  // einer noch fehlenden Quelle, fehlende Kaufpreise) werden hier NICHT als Banner
  // ausgebreitet — sie laufen in den Datencheck, den ruhigen „N zu klären"-Ort, der
  // sich beim Nachladen weiterer Quellen selbst auflöst. Sie hier zu zeigen würde
  // den Neuling überschwemmen UND den Datencheck doppeln. (Negativbestand beim
  // ersten Hochladen aller CSVs ist normal: Coins kamen von einer Börse, die noch
  // nicht importiert ist.)
  //
  // EINZIGE harte Ausnahme: ein echter Rechenfehler (nondeterministic) — da ist die
  // GESPEICHERTE Zahl selbst falsch, das kann man nicht „später" klären. Extrem
  // selten (= Engine-Bug, kein Daten-Thema), trägt also nie zur Flut bei.
  const criticalFindings = useMemo(() => {
    try {
      return auditImport(existingTxs, auditableNewTxs).filter(f => f.severity === 'critical');
    } catch { return []; }
  }, [existingTxs, auditableNewTxs]);

  // „N im Datencheck klären" = wie viele NEUE offene Punkte dieser Import dem
  // Datencheck hinzufügt (Δ Nach-Import minus Vorher, via dieselbe Quelle
  // buildDataCheck = open_transfer + no_basis). Δ statt Gesamtzahl, damit ein
  // sauberer Import nicht mit Alt-Lasten „bestraft" wird. Reine Anzeige.
  const dcOpenBefore = useMemo(
    () => buildDataCheck({ txs: existingTxs, marketData }).openCount,
    [existingTxs, marketData]
  );
  const dcOpenAfter = useMemo(
    () => buildDataCheck({ txs: [...existingTxs, ...auditableNewTxs], marketData }).openCount,
    [existingTxs, auditableNewTxs, marketData]
  );
  const newOpenPoints = Math.max(0, dcOpenAfter - dcOpenBefore);

  // Zählung pro Kategorie für die Filter-Tabs
  const categoryCounts = useMemo(() => {
    const counts = { all: txs.length, trade: 0, transfer: 0, fiat: 0, income: 0, derivatives: 0, other: 0, fee: 0 };
    txs.forEach(t => {
      const c = getTxImportCategory(t);
      if (counts[c] != null) counts[c]++;
    });
    // Import-Status-Kategorien
    counts.existing = txs.filter(t => conflictIds.has(t._editId)).length; // bereits vorhanden / übersprungen
    counts.merged   = txs.filter(t => mergedIds.has(t._editId)).length;   // zusammengeführt
    return counts;
  }, [txs, conflictIds, mergedIds]);

  const approvedCount = useMemo(
    () => Object.values(reviewStatuses).filter(v => v === 'approved').length,
    [reviewStatuses]
  );
  const approvedIds = useMemo(
    () => new Set(Object.entries(reviewStatuses).filter(([, v]) => v === 'approved').map(([id]) => Number(id))),
    [reviewStatuses]
  );

  // ── TX aus Liste entfernen ────────────────────────────────────────────────
  function removeTx(editId) {
    setTxs(prev => prev.filter(tx => tx._editId !== editId));
    setReviewStatuses(prev => { const n = { ...prev }; delete n[editId]; return n; });
  }

  // ── Voll-Editor: Zeile öffnen ─────────────────────────────────────────────
  // Tippen auf eine Zeile öffnet dieselbe Bearbeiten-Maske wie in der App
  // (TransactionEditScreen) — inkl. Börsen-Picker mit Logos, Datums-Picker und
  // allen Feldern, damit der User fehlende Daten (z. B. die Börse) ergänzen kann.
  // `coin`/`coinOut`/`coinIn` aus den `sym*`-Varianten ableiten, falls der Parser
  // nur Symbole gesetzt hat — sonst zeigt der Editor (v. a. die Tausch-Maske) leer.
  function openEditor(tx) {
    setEditingTx({
      ...tx,
      coin:    tx.coin    || tx.sym,
      coinOut: tx.coinOut || tx.symOut,
      coinIn:  tx.coinIn  || tx.symIn,
    });
  }

  // ── Voll-Editor: Änderungen übernehmen + re-validieren ─────────────────────
  function saveEditedTx(updated) {
    const editId = editingTx?._editId;
    // Per-User Synonym lernen: wenn der Typ geändert wurde, alte→neue Bezeichnung merken.
    if (uid && editingTx?.typ && updated?.typ && editingTx.typ !== updated.typ) {
      saveTypeSynonym(uid, editingTx.typ, updated.typ); // fire-and-forget
    }
    setTxs(prev => prev.map(tx => {
      if (tx._editId !== editId) return tx;
      const [revalidated] = validateTransactions([{ ...updated, _confidence: 1.0, _issues: [], _status: 'ok' }]);
      return { ...revalidated, _editId: editId };
    }));
    setEditingTx(null);
  }

  // ── Auto-Fix anwenden ─────────────────────────────────────────────────────
  function applyFix(tx, fix) {
    setTxs(prev => prev.map(t => {
      if (t._editId !== tx._editId) return t;
      const fixed = applyAutoFix(t, fix);
      const [revalidated] = validateTransactions([{ ...fixed, _confidence: 1.0, _issues: [], _status: 'ok' }]);
      return { ...revalidated, _editId: t._editId };
    }));
  }

  // ── Raw Row Toggle ────────────────────────────────────────────────────────
  function toggleRaw(editId) {
    setExpandedRaw(prev => {
      const n = new Set(prev);
      n.has(editId) ? n.delete(editId) : n.add(editId);
      return n;
    });
  }

  // ── Import ────────────────────────────────────────────────────────────────
  // mode:
  //   'valid'    — nur fehlerfreie, nicht-skipped, nicht-conflict (default)
  //   'approved' — nur explizit approved
  //   'replace'  — alle valid + die staleen Alt-TXs werden gelöscht.
  //                Conflicts werden ignoriert weil das Wipe sie eliminiert.
  async function handleImport(mode = 'valid') {
    setLoading(true);
    try {
      let toImport;
      const isReplace = mode === 'replace';
      if (mode === 'approved' && approvedCount > 0) {
        toImport = txs.filter(t => approvedIds.has(t._editId));
      } else {
        toImport = txs.filter(t => {
          if (t._status === 'error') return false;
          if (reviewStatuses[t._editId] === 'skipped') return false;
          // Replace-Mode skipt die Konflikt-Prüfung — die Alt-TXs werden
          // ja von App.js per wipeIds gelöscht, ihre contentHashes
          // sind danach nicht mehr im Datensatz.
          if (!isReplace) {
            // Konflikte (bereits importiert) standardmäßig überspringen,
            // außer User hat explizit "Trotzdem importieren" gewählt
            if (conflictIds.has(t._editId) && reviewStatuses[t._editId] !== 'force') return false;
          }
          // MoonPay-Duplikate werden NICHT hier gefiltert — sie laufen durch
          // bis addBatch (useTransactions). Dort werden die Ledger-Felder
          // (hash, address) als Wallet-Enrichment auf den MoonPay-Kauf gemerged
          // und der redundante Ledger-Eintrag verworfen. Das passiert symmetrisch
          // egal in welcher Reihenfolge MoonPay/Ledger importiert wurden.
          return true;
        });
      }
      // Markiere force-importierte TX, damit addBatch sie nicht erneut blockt
      toImport = toImport.map(t =>
        (reviewStatuses[t._editId] === 'force' || isReplace) ? { ...t, _forceImport: true } : t
      );
      // DSGVO: Korrekturen VOR dem Strippen einsammeln — nur per User gespeichert.
      // Enthält nur Schema-Diffs (Feld + alt/neu), keine fremden Transaktionsdaten.
      if (uid) {
        saveCorrections({
          uid,
          formatSignature: result.formatSignature,
          exchange:        result.exchange,
          transactions:    toImport,
        }); // fire-and-forget
      }

      // Interne + Session-Felder entfernen — nie in Firestore
      const clean = toImport.map(({ _issues, _status, _confidence, _rowIndex, _editId, _timestamp, rawCsvRow, importBatchId, reviewStatus, repairHistory, _collisionGroup, _collisionSize, ...rest }) => rest);
      // Replace-Mode: stale Alt-TXs an App.js durchreichen, damit sie VOR
      // addBatch gelöscht werden (sonst greift contentHash-Dedup ins Leere
      // bei Parser-Fix-Diffs).
      const opts = isReplace && staleWipeIds.length > 0 ? { wipeIds: staleWipeIds } : undefined;
      await onImport(clean, opts);

      await saveOrUpdateTemplate({
        uid,
        formatSignature: result.formatSignature,
        exchange: result.exchange,
        columnMappings: result.columnMappings,
        existingTemplateId: result.templateId || null,
        overallConfidence: result.overallConfidence,
      });

      if (result.reviewId && uid) {
        await completeImportReview(uid, result.reviewId, 'approved');
      }
    } finally {
      setLoading(false);
    }
  }

  // Calm: Primary = INK (T.text-Fläche, T.bg-Schrift), KEIN Rot — Rot gehört
  // app-weit der Marke. Secondary = Hairline, transparent. Press via inline scale.
  const btnPrimary = {
    flex: 1, padding: '14px', background: T.text, color: T.bg,
    border: 'none', borderRadius: mr(16), fontFamily: F.family,
    fontSize: 13.5, fontWeight: 700, letterSpacing: '-0.01em',
    cursor: loading ? 'default' : 'pointer',
    opacity: loading ? 0.6 : 1, boxShadow: 'none',
    transition: 'transform 0.12s ease, opacity 0.15s ease',
    WebkitTapHighlightColor: 'transparent',
  };
  const btnSecondary = {
    flex: 1, padding: '13px', background: 'transparent', color: T.sub,
    border: `1px solid ${T.line}`, borderRadius: mr(16), fontFamily: F.family,
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
    WebkitTapHighlightColor: 'transparent',
  };

  const hasApproved = approvedCount > 0;
  const hasErrors   = txs.some(t => t._status === 'error');
  const hasStale    = staleExistingTxs.length > 0;
  const totalImportable = hasApproved ? approvedCount : validTxs.length;

  // Pre-Flight-Summary: wie viele TX werden tatsächlich importiert vs. übersprungen?
  // Wird direkt über dem Import-Button angezeigt, damit der User nicht erst nach
  // Klick die Konflikt-Warnung weit oben im Screen sieht.
  const willSkipConflicts = useMemo(() => {
    if (hasStale) return 0; // Replace-Mode löst Konflikte auf
    let n = 0;
    conflictIds.forEach(id => {
      if (reviewStatuses[id] !== 'force') n++;
    });
    return n;
  }, [conflictIds, reviewStatuses, hasStale]);
  const willSkipErrors = txs.filter(t => t._status === 'error').length;

  // Zusammengeführt: MoonPay-/Swap-Duplikate, die NICHT ohnehin als Konflikt
  // übersprungen werden — sie laufen durch und werden in addBatch in den
  // Kauf/Swap gemergt (kein Doppelinflow). Vom „wird importiert"-Netto abziehen,
  // sonst wirkt es so, als kämen Dubletten rein.
  const willMerge = useMemo(() => {
    if (hasStale) return 0;
    const merged = new Set([...moonpayDuplicateIds, ...swapDuplicateIds]);
    let n = 0;
    merged.forEach(id => {
      if (conflictIds.has(id) && reviewStatuses[id] !== 'force') return; // schon als Skip gezählt
      if (reviewStatuses[id] === 'skipped') return;
      n++;
    });
    return n;
  }, [moonpayDuplicateIds, swapDuplicateIds, conflictIds, reviewStatuses, hasStale]);
  const netImport = Math.max(0, totalImportable - willMerge);

  // Stale-Daten priorisieren: User sieht direkt "Alte ersetzen & importieren"
  // als Primary. Ohne stale Daten regulärer "OK importieren"-Flow.
  const primaryLabel = loading ? t('imp_importing')
    : hasApproved ? t('imp_importApproved', { n: approvedCount })
    : hasStale    ? t('imp_replaceAndImport', { n: validTxs.length })
    : hasErrors    ? t('imp_importOk', { n: validTxs.length })
    : t('imp_importAll', { n: txs.length });
  const primaryMode = hasApproved ? 'approved' : hasStale ? 'replace' : 'valid';

  // Voll-Editor übernimmt den Screen, solange eine Zeile bearbeitet wird.
  // Zurück (onBack) führt ohne Speichern zur Prüfliste; onSave mergt re-validiert.
  if (editingTx) {
    return (
      <TransactionEditScreen
        tx={editingTx}
        txs={txs}
        marketData={marketData}
        onSave={saveEditedTx}
        onBack={() => setEditingTx(null)}
      />
    );
  }

  return (
    <div style={{ padding: '20px 16px 120px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: T.sub, cursor: 'pointer', fontSize: 18, padding: 0 }}>←</button>
        <div style={{
          fontFamily: F.display, fontSize: 22, fontWeight: 600,
          letterSpacing: '-0.03em', color: T.text,
        }}>{t('imp_title')}</div>
      </div>
      {fileName && (
        <div style={{
          fontSize: 11, color: T.sub, fontFamily: F.family, marginBottom: 18,
          letterSpacing: '0.02em', marginLeft: 28,
        }}>
          {fileName} · {result.exchange || t('imp_unknownExchange')}
        </div>
      )}

      <ImportSummary txs={txs} overallConfidence={overallConf} deterministic={deterministic} />

      {/* On-Chain-Saldo-Abgleich — Vertrauenssignal statt Alarm: der rekonstruierte
          Bestand wird gegen den echten Adress-Saldo (Ground-Truth von der Kette)
          geprüft. Übereinstimmung (Normalfall) = grünes „die App hat's richtig
          erfasst". Abweichung = ruhiger Hinweis + Datencheck-Brücke. Nur bei
          On-Chain-Importen vorhanden (result.reconciliation). */}
      {result.reconciliation && result.reconciliation.checked > 0 && (
        result.reconciliation.ok ? (
          <div style={{ marginBottom: 12 }}>
            <InfoBanner tone="success" title={t('imp_chain_match_title')}>
              {t('imp_chain_match_body')}
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.line}`, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {Object.entries(result.reconciliation.reported || {}).map(([sym, amt]) => (
                  <div key={sym} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: T.sub }}>{t('imp_chain_balance')}</span>
                    <span style={{ fontFamily: F.mono, fontWeight: 600, color: T.text }}>{fmtC(amt)} {sym}</span>
                  </div>
                ))}
              </div>
            </InfoBanner>
          </div>
        ) : (
          <div style={{ marginBottom: 12 }}>
            <InfoBanner tone="warning" title={t('imp_chain_mismatch_title')}>
              {t('imp_chain_mismatch_body')}
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.line}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(result.reconciliation.mismatches || []).map(m => (
                  <div key={m.sym} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: T.sub }}>{t('imp_chain_computed')}</span>
                      <span style={{ fontFamily: F.mono, fontWeight: 600, color: T.text }}>{fmtC(m.computed)} {m.sym}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: T.sub }}>{t('imp_chain_onchain')}</span>
                      <span style={{ fontFamily: F.mono, fontWeight: 600, color: T.text }}>{fmtC(m.reported)} {m.sym}</span>
                    </div>
                  </div>
                ))}
              </div>
            </InfoBanner>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              marginTop: 8, padding: '12px 4px',
              borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}`,
              color: T.sub, fontFamily: F.family, fontSize: 12.5,
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.gold || '#F5A623', flexShrink: 0 }} />
              <span>{t('imp_chain_mismatch_hint')}</span>
            </div>
          </div>
        )
      )}

      {/* Echter Rechenfehler (sehr selten) — die EINZIGE harte Warnung, die bleibt:
          die gespeicherte Zahl wäre falsch, das lässt sich nicht „später" klären. */}
      {criticalFindings.length > 0 && (
        <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {criticalFindings.map((f, i) => {
            const params = { coins: (f.coins || []).join(', '), n: f.count, unknown: f.unknownPos, total: f.totalPos };
            return (
              <InfoBanner key={`${f.code}-${i}`} tone="danger" title={t(`imp_audit_${f.code}_title`, params)}>
                {t(`imp_audit_${f.code}_body`, params)}
              </InfoBanner>
            );
          })}
        </div>
      )}

      {/* Ruhige Brücke zum Datencheck: alltägliche Lücken (fehlende Quelle/Kaufpreis)
          werden NICHT hier ausgebreitet, sondern dort im eigenen Tempo geklärt.
          Eine Zeile, kein Stapel, kein Druck — der Import bleibt einladend. */}
      {newOpenPoints > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          marginBottom: 14, padding: '12px 4px',
          borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}`,
          color: T.sub, fontFamily: F.family, fontSize: 12.5,
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.gold || '#F5A623', flexShrink: 0 }} />
          <span>{newOpenPoints === 1 ? t('imp_datacheck_pointer_one') : t('imp_datacheck_pointer_other', { n: newOpenPoints })}</span>
        </div>
      )}

      {/* Stale-Parser-Version: bestehende TXs aus derselben Börse aus älterem
          Parser. Default-Aktion ist Ersetzen — verhindert Doppel-Importe nach
          Parser-Fixes (Symptom: zwei SHIB-Verkäufe gleichen Sekunden-Timestamps,
          alter Parser hat nur einen erfasst, neuer beide → contentHashes
          unterscheiden sich → Dedup greift nicht). */}
      {staleExistingTxs.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <InfoBanner tone="info" title={t('imp_staleTitle')}>
            {t('imp_staleBody', { n: validTxs.length, exchange: result?.exchange || '' })}
          </InfoBanner>
        </div>
      )}

      {/* Konflikt-Sektion: bereits importierte TX (Re-Import-Schutz).
          NICHT zeigen wenn ohnehin ersetzt wird (staleExistingTxs) — dann werden
          sie aktualisiert statt übersprungen, das Banner wäre widersprüchlicher Lärm.
          NICHT zeigen während loading — Firestore-Listener aktualisiert existingTxs
          bevor der Screen sich schließt → Race Condition die fälschlich Konflikte meldet. */}
      {conflictIds.size > 0 && staleExistingTxs.length === 0 && !loading && (
        <div style={{ marginBottom: 12 }}>
          <InfoBanner
            tone="info"
            title={t('imp_conflictTitle')}
          >
            {conflictIds.size === 1
              ? t('imp_conflictBody_one', { n: conflictIds.size })
              : t('imp_conflictBody_other', { n: conflictIds.size })}
          </InfoBanner>
        </div>
      )}

      {/* MoonPay-Duplikat-Sektion: Ledger-Einzahlung mit passendem MoonPay-Kauf */}
      {moonpayDuplicateIds.size > 0 && (
        <div style={{ marginBottom: 12 }}>
          <InfoBanner
            tone="info"
            title={t('imp_moonpayTitle', { n: moonpayDuplicateIds.size })}
          >
            {t('imp_moonpayBody')}
          </InfoBanner>
        </div>
      )}

      {/* Swap-Settlement-Sektion: On-Chain-Eingang doppelt den Output eines Swaps */}
      {swapDuplicateIds.size > 0 && (
        <div style={{ marginBottom: 12 }}>
          <InfoBanner
            tone="info"
            title={t('imp_swapMergeTitle', { n: swapDuplicateIds.size })}
          >
            {t('imp_swapMergeBody')}
          </InfoBanner>
        </div>
      )}

      {/* Technische Banner (Präzision / Kollisionsgruppen) bewusst entfernt — zu
          technisch für den Kunden. Die FIFO-Reihenfolge ist durch den
          deterministischen Pre-Sort (Invariant 7) abgesichert; identische
          Zeitstempel-Duplikate fängt die contentHash-Dedup. Kein Kunden-Alarm nötig. */}

      {/* Fortschrittsanzeige */}
      {approvedCount > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: T.muted, fontFamily: F.family, marginBottom: 4 }}>
            {t('imp_reviewedOf', { n: approvedCount, total: txs.length })}
          </div>
          <div style={{ height: 3, borderRadius: 4, background: T.border, overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 4, background: T.green, width: `${(approvedCount / txs.length) * 100}%`, transition: 'width 0.3s ease' }} />
          </div>
        </div>
      )}

      {/* Status-Filter NUR wenn es etwas zu filtern gibt (Warnungen/Fehler) —
          ein sauberer Import braucht keinen ✓/⚠/✗-Schalter (wäre nur Lärm). */}
      {txs.some(t => t._status === 'warning' || t._status === 'error') && (
        <div style={{ marginBottom: 8 }}>
          <Segmented
            fluid
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all',     label: `${t('imp_filterAll')} (${txs.length})`, n: 1 },
              { value: 'ok',      label: `✓ (${txs.filter(t => t._status === 'ok').length})`, n: txs.filter(t => t._status === 'ok').length },
              { value: 'warning', label: `⚠ (${txs.filter(t => t._status === 'warning').length})`, n: txs.filter(t => t._status === 'warning').length },
              { value: 'error',   label: `✗ (${txs.filter(t => t._status === 'error').length})`, n: txs.filter(t => t._status === 'error').length },
            ].filter(o => o.n > 0)}
          />
        </div>
      )}

      {/* Filter Tabs — Kategorie (neues Design: Segmented mit Pill-Motion) */}
      <div style={{ marginBottom: 12, overflowX: 'auto' }} className="hide-scrollbar">
        <Segmented
          value={category}
          onChange={setCategory}
          options={[
            { value: 'all',         label: `${t('imp_filterAll')} (${categoryCounts.all})` },
            { value: 'trade',       label: `${CATEGORY_LABELS.trade} (${categoryCounts.trade})` },
            { value: 'transfer',    label: `${CATEGORY_LABELS.transfer} (${categoryCounts.transfer})` },
            { value: 'fiat',        label: `${CATEGORY_LABELS.fiat} (${categoryCounts.fiat})` },
            { value: 'income',      label: `${CATEGORY_LABELS.income} (${categoryCounts.income})` },
            { value: 'derivatives', label: `${CATEGORY_LABELS.derivatives} (${categoryCounts.derivatives})` },
            { value: 'other',       label: `${CATEGORY_LABELS.other} (${categoryCounts.other})` },
            { value: 'merged',      label: `${t('imp_cat_merged')} (${categoryCounts.merged})` },
            { value: 'existing',    label: `${t('imp_cat_existing')} (${categoryCounts.existing})` },
          ].filter(c => c.value === 'all' || categoryCounts[c.value] > 0)}
        />
      </div>

      {/* Transaction List — flach (Haarlinien-Zeilen, kein umrandeter Container) */}
      <div style={{
        borderTop: `1px solid ${T.line}`,
        marginBottom: 16, maxHeight: 540, overflowY: 'auto',
      }}>
        {filtered.slice(0, visibleCount).map((tx) => {
          const isSwap  = tx.typ === 'swap';
          const sym     = tx.sym || tx.coin || tx.symOut || tx.coinOut;
          const isApproved = reviewStatuses[tx._editId] === 'approved';
          const isSkipped  = reviewStatuses[tx._editId] === 'skipped';
          const rowBg = isApproved ? `${T.green}0D`
            : isSkipped  ? 'transparent'
            : tx._status === 'error'   ? 'rgba(231,76,60,0.07)'
            : tx._status === 'warning' ? 'rgba(245,166,35,0.07)'
            : 'transparent';
          const fixes = computeAutoFixes(tx, tx._issues || []);
          const hasRaw = !!tx.rawCsvRow;
          const rawExpanded = expandedRaw.has(tx._editId);

          return (
            <div key={tx._editId} style={{ borderBottom: `1px solid ${T.line}` }}>
              <div
                onClick={() => openEditor(tx)}
                title={t('imp_clickToEdit')}
                onMouseEnter={e => { e.currentTarget.style.background = T.itemHover || 'rgba(255,255,255,0.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}
                style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '13px 4px',
                background: rowBg,
                opacity: isSkipped ? 0.4 : 1,
                borderLeft: isApproved ? `3px solid ${T.green}` : '3px solid transparent',
                color: T.text, cursor: 'pointer',
                transition: 'background 0.12s ease',
              }}>
                {/* Coin-Icon — CoinGecko-Bild (wie Dashboard) */}
                {isSwap ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                    <CoinImage sym={tx.coinOut || tx.symOut} marketData={marketData} size={26} />
                    <span style={{ color: T.sub, fontSize: 13, margin: '0 2px' }}>›</span>
                    <CoinImage sym={tx.coinIn || tx.symIn} marketData={marketData} size={26} />
                  </div>
                ) : (
                  <CoinImage sym={sym} marketData={marketData} size={36} />
                )}

                {/* Info-Block */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                    fontFamily: F.display, fontSize: 13.5, fontWeight: 600,
                    letterSpacing: '-0.02em', color: T.text, marginBottom: 2,
                  }}>
                    {isSwap ? (
                      <span>{(tx.coinOut || tx.symOut)} → {(tx.coinIn || tx.symIn)}</span>
                    ) : (
                      <span style={{ fontFamily: F.display, fontSize: 13.5, fontWeight: 600, color: T.text, letterSpacing: '-0.02em' }}>
                        {tx.coin || tx.sym || '—'}
                      </span>
                    )}
                    <TypeBadge typ={tx.typ} />
                  </div>
                  <div style={{
                    fontSize: 11, color: T.sub, fontFamily: F.family,
                    display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
                  }}>
                    <span style={{ fontSize: 11, color: T.sub }}>{tx.datum || '—'}</span>
                    <span style={{ opacity: 0.5 }}>·</span>
                    <ExchangeLogo name={tx.exchange} size={12} />
                    <span style={{ fontSize: 11, color: tx.exchange ? T.sub : (T.gold || '#F5A623'), fontWeight: tx.exchange ? 400 : 600 }}>
                      {tx.exchange || t('imp_unknownExchange')}
                    </span>
                    {!isSwap && tx.menge != null && (
                      <>
                        <span style={{ opacity: 0.5 }}>·</span>
                        <span style={{ fontFamily: F.mono, fontSize: 11, color: T.sub }}>
                          {tx.menge ? fmtC(tx.menge) : '0'}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Preis rechts */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{
                    fontFamily: F.mono, fontSize: 13.5, fontWeight: 600,
                    color: T.text, letterSpacing: '-0.02em',
                  }}>
                    {tx.preis ? fmt(tx.preis) : (IMPORT_FIAT_SET.has(String(tx.coin || tx.sym || '').toUpperCase()) && tx.menge ? fmt(tx.menge) : '0')} €
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end',
                    marginTop: 3,
                  }}>
                    <ConfidenceBadge value={tx._confidence ?? 0} />
                  </div>
                </div>

                {/* Aktion: Zeile aus der Liste entfernen — borderloser Icon-Button
                    im Navbar-Stil (groß, sichtbar, Press-Squish, rot beim Hover). */}
                <button
                  onClick={(e) => { e.stopPropagation(); removeTx(tx._editId); }}
                  aria-label={t('imp_remove')}
                  title={t('imp_remove')}
                  onMouseEnter={e => { e.currentTarget.style.color = T.red; }}
                  onMouseLeave={e => { e.currentTarget.style.color = T.mute; }}
                  onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.88)'; }}
                  onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                  style={{
                    width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                    background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: T.mute, WebkitTapHighlightColor: 'transparent',
                    transition: 'color 0.15s ease, transform 0.16s cubic-bezier(.16,1,.3,1)',
                  }}
                >
                  <Icon d={Icons.close} size={22} stroke="currentColor" sw={2.4} />
                </button>
              </div>

              {/* Auto-Fix Chip + Raw CSV Toggle */}
              {(fixes.length > 0 || hasRaw) && (
                <div style={{ display: 'flex', gap: 6, padding: '4px 14px 6px', background: rowBg, flexWrap: 'wrap' }}>
                  {fixes.map((fix, fi) => (
                    <button key={fi} onClick={() => applyFix(tx, fix)} style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: mr(16), cursor: 'pointer',
                      background: fix.confidence === 'high' ? T.greenDim : '#2B1F0A',
                      border: `1px solid ${fix.confidence === 'high' ? T.green : '#F5A623'}44`,
                      color: fix.confidence === 'high' ? T.green : '#F5A623',
                      fontFamily: F.family, fontWeight: 600,
                    }}>
                      ⚡ {fix.reason}
                    </button>
                  ))}
                  {hasRaw && (
                    <button onClick={() => toggleRaw(tx._editId)} style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: mr(16), cursor: 'pointer',
                      background: 'none', border: `1px solid ${T.border}`,
                      color: T.muted, fontFamily: F.family,
                    }}>
                      CSV {rawExpanded ? '▲' : '▼'}
                    </button>
                  )}
                </div>
              )}

              {/* Raw CSV Row */}
              {rawExpanded && tx.rawCsvRow && (
                <div style={{ padding: '6px 14px 8px', background: '#0A0A0F', borderTop: `1px solid ${T.border}` }}>
                  <pre style={{
                    fontSize: 9, color: T.muted, fontFamily: F.mono || F.family,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, lineHeight: 1.5,
                  }}>
                    {tx.rawCsvRow}
                  </pre>
                </div>
              )}
            </div>
          );
        })}

        {/* Zeige mehr */}
        {filtered.length > visibleCount && (
          <button
            onClick={() => setVisible(v => v + 100)}
            style={{
              display: 'block', width: '100%', padding: '10px 14px',
              background: T.surface, fontSize: 11, color: T.muted,
              fontFamily: F.family, textAlign: 'center', border: 'none',
              cursor: 'pointer', borderTop: `1px solid ${T.border}`,
            }}
          >
            {t('imp_showMore', { n: filtered.length - visibleCount })}
          </button>
        )}
        {filtered.length === 0 && (
          <div style={{ padding: '20px', textAlign: 'center', color: T.muted, fontFamily: F.family, fontSize: 13 }}>
            {t('imp_emptyCategory')}
          </div>
        )}
      </div>

      {/* Hinweis bei Low Confidence — nicht bei deterministischen On-Chain-Importen */}
      {!deterministic && overallConf < 0.55 && (
        <div style={{ marginBottom: 14 }}>
          <InfoBanner tone="danger" title={t('imp_lowConfTitle')}>
            {t('imp_lowConfBody')}
          </InfoBanner>
        </div>
      )}

      {/* Pre-Flight-Summary: Was passiert beim Klick auf Import?
          Sitzt direkt über dem Button, damit der User nicht erst nach Klick
          die Konflikt-Warnung weit oben im Screen sieht. */}
      {(willSkipConflicts > 0 || willSkipErrors > 0 || willMerge > 0) && (
        <div style={{
          marginBottom: 14, padding: '13px 4px',
          borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}`,
          display: 'flex', alignItems: 'baseline', gap: 18, flexWrap: 'wrap',
          fontFamily: F.family,
        }}>
          <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{
              fontSize: 11.5, fontWeight: 600, color: T.sub,
            }}>{t('imp_willImport')}</span>
            <span style={{
              fontFamily: F.display, fontSize: 16, fontWeight: 800,
              letterSpacing: '-0.02em', color: T.green || T.gain,
            }}>{netImport}</span>
          </div>
          {willMerge > 0 && (
            <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{
                fontSize: 9.5, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: T.mute,
              }}>{t('imp_willMerge')}</span>
              <span style={{
                fontFamily: F.display, fontSize: 16, fontWeight: 800,
                letterSpacing: '-0.02em', color: T.text,
              }}>{willMerge}</span>
            </div>
          )}
          {willSkipConflicts > 0 && (
            <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{
                fontSize: 9.5, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: T.mute,
              }}>{t('imp_alreadyExistsSkipped')}</span>
              <span style={{
                fontFamily: F.display, fontSize: 16, fontWeight: 800,
                letterSpacing: '-0.02em', color: T.gold || '#F5A623',
              }}>{willSkipConflicts}</span>
            </div>
          )}
          {willSkipErrors > 0 && (
            <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{
                fontSize: 9.5, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: T.mute,
              }}>{t('imp_withErrorSkipped')}</span>
              <span style={{
                fontFamily: F.display, fontSize: 16, fontWeight: 800,
                letterSpacing: '-0.02em', color: T.red,
              }}>{willSkipErrors}</span>
            </div>
          )}
        </div>
      )}

      {/* Aktions-Buttons */}
      <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            style={btnSecondary}
            onClick={onCancel}
            disabled={loading}
            onMouseEnter={e => { if (loading) return; e.currentTarget.style.background = T.itemHover || 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = T.text; e.currentTarget.style.borderColor = T.text; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.sub; e.currentTarget.style.borderColor = T.line; }}
          >{t('cancel')}</button>
          <button
            style={btnPrimary}
            onClick={() => handleImport(primaryMode)}
            disabled={loading || totalImportable === 0}
            onMouseEnter={e => { if (loading || totalImportable === 0) return; e.currentTarget.style.opacity = '0.9'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = loading ? '0.6' : '1'; }}
            onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.985)'; }}
            onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {primaryLabel}
          </button>
        </div>
        {hasApproved && (
          <button
            onClick={() => handleImport('valid')}
            disabled={loading}
            style={{ background: 'none', border: 'none', color: T.muted, fontSize: 11, fontFamily: F.family, cursor: 'pointer', textAlign: 'center', textDecoration: 'underline' }}
          >
            {t('imp_importAllNoErrors', { n: validTxs.length })}
          </button>
        )}
      </div>
    </div>
  );
}
